import { RefObject, useImperativeHandle, useRef, useState } from 'react'
import { createContainer } from 'unstated-next'
import moment from 'moment'
import { api } from '../../infrastructure/api'
import guid, { Guid } from '../../infrastructure/guid'
import { usePrevious } from '../../infrastructure/customHooks'
import { snackbars } from '../../infrastructure/snackbars'
import { t } from '../../infrastructure/i18nextHelper'
import { UserContextContainer } from '../../infrastructure/signIn/userContext'
import { Site } from '../administration/masterData/models'
import {
    ProductRecipeItem,
    RecipeLocalProduct,
    RoadSalesProduct,
    RoadSalesProductRecipe
} from './recipeModels'

type RecipeDialogRef = {
    open: (id: Guid) => void
    openNew: () => void
    close: () => void
}

let dialogRef: RefObject<RecipeDialogRef> | null = null

let hasSamePerimeter = (existing: RoadSalesProductRecipe, validFrom: string, site: string | null) =>
    moment(existing.validFrom).isSame(validFrom, 'day')
    && (existing.site ?? '').toLowerCase() === (site ?? '').toLowerCase()

function useRecipeEdit() {
    let userStore = UserContextContainer.useContainer()
    let [isOpen, setIsOpen] = useState(false)
    let [isNew, setIsNew] = useState(false)
    let [editorState, setEditorState] = useState<RoadSalesProductRecipe | null>(null)
    let [oneCompanyInCountry, setOneCompanyInCountry] = useState(false)
    let [roadSalesProducts, setRoadSalesProducts] = useState<RoadSalesProduct[]>([])
    let [localProducts, setLocalProducts] = useState<RecipeLocalProduct[]>([])
    let [sites, setSites] = useState<Site[]>([])
    let [isReplaceDialogOpen, setIsReplaceDialogOpen] = useState(false)
    let referentialsLoaded = useRef(false)
    let previousIsOpen = usePrevious(isOpen)

    let loadReferentials = async () => {
        if (referentialsLoaded.current) return

        let [roadSalesProductsResult, localProductsResult, sitesResult] = await Promise.all([
            api.get<RoadSalesProduct[]>('masterdata/roadSalesProductRecipe/roadSalesProduct'),
            api.get<RecipeLocalProduct[]>('masterdata/roadSalesProductRecipe/localProduct'),
            api.get<Site[]>('masterdata/roadSalesProductRecipe/site')
        ])
        setRoadSalesProducts(roadSalesProductsResult)
        setLocalProducts(localProductsResult)
        setSites(sitesResult)
        referentialsLoaded.current = true
    }

    let open = async (id: Guid) => {
        setIsNew(false)
        setEditorState(null)
        setIsOpen(true)
        window.history.pushState({}, '', '?openRecipe=' + id)
        await loadReferentials()
        let recipe = await api.get<RoadSalesProductRecipe>(`masterdata/roadSalesProductRecipe/${id}`)
        setEditorState({ ...recipe, items: recipe.items.map(item => ({ ...item })) })
    }

    let createEmptyForm = (): RoadSalesProductRecipe => ({
        id: guid.empty,
        roadSalesProductId: null,
        description: '',
        validFrom: null,
        site: null,
        active: true,
        items: [{ productId: null, percentage: 1 }]
    })

    let openNew = async () => {
        setIsNew(true)
        setEditorState(createEmptyForm())
        setIsReplaceDialogOpen(false)
        setIsOpen(true)
        await loadReferentials()
    }

    let close = () => {
        setIsOpen(false)
        setIsNew(false)
        setEditorState(null)
        setIsReplaceDialogOpen(false)
        window.history.pushState({}, document.title, window.location.pathname)
    }

    let updateForm = (update: (form: RoadSalesProductRecipe) => RoadSalesProductRecipe) => setEditorState(current =>
        current ? update(current) : current)

    let addComponent = () => updateForm(form => ({
        ...form,
        items: [...form.items, { productId: null, percentage: null }]
    }))

    let removeComponent = (index: number) => updateForm(form => ({
        ...form,
        items: form.items.filter((_, itemIndex) => itemIndex !== index)
    }))

    let setComponent = (index: number, component: ProductRecipeItem) => updateForm(form => ({
        ...form,
        items: form.items.map((item, itemIndex) => itemIndex === index ? component : item)
    }))

    let validate = (): string | null => {
        if (!editorState) return null
        let form = editorState
        if (!form.roadSalesProductId) return 'recipes.error.invalidRoadSalesProduct'
        if (!form.description.trim()) return 'recipes.error.emptyDescription'
        if (form.description.trim().length > 300) return 'recipes.error.descriptionTooLong'
        if (!form.validFrom || !moment(form.validFrom).isValid()) return 'recipes.error.missingValidFrom'
        if (form.items.some(item => item.percentage != null && item.percentage < 0)) return 'recipes.error.negativePercentage'
        if (form.items.some(item => !item.productId)) return 'recipes.error.invalidComponent'

        let productIds = form.items.map(item => item.productId).filter((id): id is Guid => !!id)
        if (new Set(productIds).size !== productIds.length) return 'recipes.error.duplicateComponent'
        if (!form.items.some(item => (item.percentage ?? 0) > 0)) return 'recipes.error.noComponent'
        return null
    }

    let save = async () => {
        if (!editorState) return
        let validationError = validate()
        if (validationError) {
            snackbars.warning(t(validationError))
            return
        }

        let currentForm = editorState
        let existingRecipes = await api.get<RoadSalesProductRecipe[]>(
            `masterdata/roadSalesProductRecipe?roadSalesProductIds=${currentForm.roadSalesProductId}`)
        let hasSameBusinessKey = existingRecipes.some(existing =>
            existing.id !== currentForm.id
            && hasSamePerimeter(existing, currentForm.validFrom!, currentForm.site))

        if (hasSameBusinessKey) {
            setIsReplaceDialogOpen(true)
            return
        }

        await confirmSave()
    }

    let confirmSave = async () => {
        if (!editorState) return
        let form = editorState
        await api.post('masterdata/roadSalesProductRecipe', {
            recipe: {
                id: form.id,
                roadSalesProductId: form.roadSalesProductId,
                description: form.description,
                validFrom: form.validFrom,
                site: form.site,
                active: form.active,
                country: userStore.currentCountry,
                items: form.items.map(item => ({ productId: item.productId, percentage: item.percentage ?? 0 }))
            }
        })
        snackbars.success(t('recipes.saved'))
        setIsReplaceDialogOpen(false)
        close()
    }

    dialogRef = useRef<RecipeDialogRef>(null)
    useImperativeHandle(dialogRef, () => ({ open, openNew, close }))

    return {
        isOpen,
        isNew,
        editorState,
        oneCompanyInCountry,
        setOneCompanyInCountry,
        updateForm,
        roadSalesProducts,
        localProducts,
        sites,
        isReplaceDialogOpen,
        setIsReplaceDialogOpen,
        addComponent,
        removeComponent,
        setComponent,
        validate,
        save,
        confirmSave,
        previousIsOpen
    }
}

export let recipeDialog = {
    open: (id: Guid) => dialogRef?.current?.open(id),
    openNew: () => dialogRef?.current?.openNew(),
    close: () => dialogRef?.current?.close()
}

export let RecipeEditContainer = createContainer(useRecipeEdit)
